import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { useQueryParams } from 'use-query-params'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ContentLayout } from '@app/layouts'
import { LntButton, LntPopover } from '@app/components/base'
import { ExtendedTable, Header, Subheader, TableSettingsToolbar } from '@app/components'
import { TransferSettingsDrawer } from '@app/drawers'

import { PRICING_KEY } from '@app/constants/queryKeys'
import { BUTTON_TYPE, SIZE, dateFormat } from '@app/constants'
import { TABLE_SETTING_KEYS } from '@app/constants/tableSettingKeys'
import { responseStatus } from '@app/constants/responseStatus'

import { checkPricingToolsSend, getProductPricingList, postPricingToolsSend } from '@app/api/routes/pricing'

import { useNotification, usePaginationQueryParams, useTableSearch, useTableSetting, useToggle } from '@app/hooks'
import { getDayOfCurrentWeek } from '@app/utils/date'
import { isEmptyArray, isNullable } from '@app/utils'

import { columnsSearchSchema, tableSearchQuerySchema, topFiltersQuerySchema } from './configs'
import { adaptPricingToolsSendData, adaptProductPricingTableData, getColumns } from './lib'
import { TopFilters, SendPopoverContent } from './ui'

import type { FiltersParams } from '@app/types/filter'
import type { GetProductPricingListParams } from '@app/api/routes/pricing/types'
import type { AxiosError } from 'axios'
import type { ErrorResponseDto } from '@app/api/dtos'

const title = 'Передача результатов'

const columns = getColumns()

const currentMonday = getDayOfCurrentWeek(1).format(dateFormat.YYYY_MM_DD)
const currentSunday = getDayOfCurrentWeek(7).format(dateFormat.YYYY_MM_DD)

export const TransferResultsPage: React.FC = () => {
  const settingsToggle = useToggle()
  const sendPopoverToogle = useToggle()

  const [pricingToolWarningText, setPricingToolWarningText] = useState<string>()

  const queryClient = useQueryClient()
  const notification = useNotification()

  const [queryFilters, setQueryFilters] = useQueryParams(topFiltersQuerySchema)

  const { paginationParams, pagination, setPagination, resetCurrentPage } = usePaginationQueryParams()

  const productPricingListParams = useMemo(
    () => ({ ...paginationParams, ...queryFilters }),
    [paginationParams, queryFilters]
  ) as GetProductPricingListParams

  const { data: productPricingTableData, isLoading: isProductPricingDataLoading } = useQuery({
    queryKey: [PRICING_KEY.productPricingListItems, productPricingListParams],
    queryFn: async () => await getProductPricingList(productPricingListParams),
    select: resultsData => adaptProductPricingTableData(resultsData)
  })

  const postPricingToolData = useMemo(
    () => adaptPricingToolsSendData(queryFilters as GetProductPricingListParams),
    [queryFilters]
  )

  const clearPricingToolWarningText = (): void => {
    setPricingToolWarningText('')
  }

  const resetTransferSetup = (): void => {
    clearPricingToolWarningText()
    settingsToggle.setUntoggled()
  }

  const checkPricingToolsSendMutation = useMutation({
    mutationFn: checkPricingToolsSend,
    onSuccess: sendPopoverToogle.setToggled,
    onError: ({ response }: AxiosError<ErrorResponseDto>) => {
      if (isNullable(response)) return

      const { status } = response
      const { detail } = response.data

      if (status !== responseStatus.UNPROCESSABLE_CONTENT) return

      setPricingToolWarningText(detail)
      sendPopoverToogle.setToggled()
    }
  })

  const postPricingToolsSendMutation = useMutation({
    mutationFn: postPricingToolsSend,
    onSuccess: async () => {
      await queryClient.invalidateQueries([PRICING_KEY.productPricingListItems])

      notification.success({
        message: 'Успешно отправлено.'
      })

      sendPopoverToogle.setUntoggled()
      clearPricingToolWarningText()
    }
  })

  const isShowSentButton = useMemo(() => {
    const { monitoringKindIds, monitoringTypeIds, monitoringStartAtBefore, monitoringStartAtAfter } =
      productPricingListParams

    const existMonitoringsFilterParams = !isEmptyArray(monitoringKindIds) || !isEmptyArray(monitoringTypeIds)
    const existDatesFilterParams = !isNullable(monitoringStartAtBefore) && !isNullable(monitoringStartAtAfter)

    return existDatesFilterParams && existMonitoringsFilterParams
  }, [productPricingListParams])

  const handleCloseSedPopover = (): void => {
    sendPopoverToogle.setUntoggled()
  }

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const handleFiltersChange = (filters: FiltersParams): void => {
    setQueryFilters(filters)
    resetCurrentPage()
    clearPricingToolWarningText()
  }

  const {
    items,
    defaultItems,
    columns: modifiedColumns,
    onModify
  } = useTableSetting(TABLE_SETTING_KEYS.transferResultsPage, columns)

  const {
    columns: columnsWithSearchTool,
    filteredDataSource: filteredProductPricingTableDataSource,
    isSearchToolOpened,
    toggleSearchTool
  } = useTableSearch({
    columns: modifiedColumns,
    dataSource: productPricingTableData?.dataSource,
    columnsSearchSchema,
    tableSearchQuerySchema
  })

  useEffect(() => {
    const handleSetDefaultFilters = (): void => {
      const isEmptyDateFilters = !queryFilters.monitoringStartAtAfter && !queryFilters.monitoringStartAtBefore

      if (isEmptyDateFilters) {
        setQueryFilters(prev => ({
          ...prev,
          monitoringStartAtAfter: currentMonday,
          monitoringStartAtBefore: currentSunday
        }))
      }
    }

    handleSetDefaultFilters()
  }, [])

  const subheaderToolbar = [
    <LntButton
      key='settings-transfer'
      type={ BUTTON_TYPE.secondary }
      size={ SIZE.small }
      onClick={ settingsToggle.setToggled }
    >
      Настройки передачи
    </LntButton>
  ]

  const toolbar = [
    <Fragment key='transfer-results-control-buttons'>
      <LntPopover
        trigger='click'
        open={ sendPopoverToogle.value }
        onOpenChange={ open => !open && sendPopoverToogle.setUntoggled() }
        content={
          <SendPopoverContent
            warning={ pricingToolWarningText }
            isLoading={ postPricingToolsSendMutation.isLoading }
            onClose={ handleCloseSedPopover }
            onSubmit={ () => postPricingToolsSendMutation.mutate(postPricingToolData) }
          />
        }
      >
        <LntButton
          type={ BUTTON_TYPE.primary }
          size={ SIZE.small }
          disabled={ !isShowSentButton }
          loading={ checkPricingToolsSendMutation.isLoading }
          onClick={ () => checkPricingToolsSendMutation.mutate(postPricingToolData) }
        >
          Отправить в Pricing Tool
        </LntButton>
      </LntPopover>
    </Fragment>,

    <TableSettingsToolbar
      key='table-settings-toolbar'
      tableSettingButtonProps={ { items, defaultItems, onAccept: onModify } }
      tableSearchButtonProps={ { active: isSearchToolOpened, onClick: toggleSearchTool } }
    />
  ]

  return (
    <>
      <Header controlProps={ { title } } />

      <Subheader toolbar={ subheaderToolbar } />

      <ContentLayout>
        <TopFilters
          onSearch={ handleFiltersChange }
          initialFilters={ queryFilters as FiltersParams }
        />

        <ExtendedTable
          loading={ isProductPricingDataLoading }
          columns={ columnsWithSearchTool }
          dataSource={ filteredProductPricingTableDataSource }
          toolbar={ toolbar }
          pagination={ {
            ...pagination,
            total: productPricingTableData?.count,
            onChange: handlePaginationChange
          } }
        />
      </ContentLayout>

      <TransferSettingsDrawer
        open={ settingsToggle.value }
        onClose={ settingsToggle.setUntoggled }
        onSave={ resetTransferSetup }
      />
    </>
  )
}
